import { Stack, Text, Heading, Label, InlineStack, Panel, PanelSection } from '@customerio/pluma-components/react';
import type { ServicesApiEnvHealthResponse } from '@/services/api/services-api';
import { delayStatus, HIGH_IMPACT_CATEGORIES } from '../utils';
import { HealthScoreGauge } from './health-score-gauge';

const STATUS_CONFIG: Record<ServicesApiEnvHealthResponse['health'], { heading: string; description: string }> = {
  healthy: {
    heading: 'Healthy',
    description: 'Everything is running smoothly. No issues detected.',
  },
  busy: {
    heading: 'Busy',
    description: 'Your workspace is processing a higher volume than usual.',
  },
  degraded: {
    heading: 'Slow performance',
    description: 'Your workspace has pending issues that may be impacting performance.',
  },
};

function countSeverities(data: ServicesApiEnvHealthResponse): { high: number; medium: number; low: number } {
  const allCategories = [...data.processing.categories, ...data.egress.categories];
  let high = 0;
  let medium = 0;
  let low = 0;

  for (const cat of allCategories) {
    const status = delayStatus(cat.delay_seconds, cat.pending);
    if (status.color === 'red') high++;
    else if (status.color === 'yellow' && HIGH_IMPACT_CATEGORIES.has(cat.name)) medium++;
    else if (status.color === 'yellow') low++;
  }

  return { high, medium, low };
}

interface SummaryPanelProps {
  data: ServicesApiEnvHealthResponse;
}

export function SummaryPanel({ data }: SummaryPanelProps) {
  const config = STATUS_CONFIG[data.health];
  const severities = countSeverities(data);
  const hasIssues = severities.high > 0 || severities.medium > 0 || severities.low > 0;

  return (
    <Panel>
      <PanelSection>
        <Stack gap="200">
          <HealthScoreGauge data={data} />
          <Stack gap="100">
            <Heading level="2">{config.heading}</Heading>
            <Text size="s" color="subtle">
              {config.description}
            </Text>
          </Stack>
          {hasIssues ? (
            <InlineStack gap="100" wrap>
              {severities.high > 0 ? <Label color="red">{severities.high} high priority</Label> : null}
              {severities.medium > 0 ? <Label color="yellow">{severities.medium} medium</Label> : null}
              {severities.low > 0 ? <Label color="grey">{severities.low} low</Label> : null}
            </InlineStack>
          ) : null}
        </Stack>
      </PanelSection>
    </Panel>
  );
}
